/**
 * Постоянный текст отчёта и подписи к кодам.
 *
 * API отдаёт коды — `URGENT`, `ACTIVITY`, `RED`, `MANUAL_EDIT_DAYS`, — а
 * слова живут здесь. Так формулировку правят без релиза бэкенда, а
 * страница не разъезжается сама с собой: одна подпись на все места, где
 * код встречается.
 *
 * @format
 */

import type {
  CampaignColor,
  EditsStatusReason,
  ExpectationMetric,
  KpiName,
  ScorePartKey,
  ScoreZoneKey,
  Section,
  SectionId,
  Severity,
} from './types';
import {SECTION_IDS} from './types';

/**
 * Код тяжести → ключ палитры. Тона в теме названы строчными (`good`,
 * `alert`, `bad`), в схеме — прописными; переводим в одном месте, чтобы
 * `theme.palette[tone]` не собирался по строкам вручную.
 */
export type PaletteTone = 'good' | 'alert' | 'bad';

export function paletteTone(severity: Severity): PaletteTone {
  if (severity === 'GOOD') return 'good';
  if (severity === 'ALERT') return 'alert';
  return 'bad';
}

/* ─────────────────────────── шкала балла ─────────────────────────── */

/** Подпись полосы шкалы: стоит под линейкой и в легенде. */
export const SCORE_ZONE_LABEL: Record<ScoreZoneKey, string> = {
  URGENT: 'Требует срочного вмешательства',
  NEEDS_ATTENTION: 'Требует внимания',
  NORMAL: 'Норма',
  EXCELLENT: 'Отлично',
};

/**
 * Вердикт — то, что читается раньше числа. Отличается от подписи полосы:
 * подпись называет диапазон, вердикт говорит о кабинете.
 */
export const SCORE_ZONE_VERDICT: Record<ScoreZoneKey, string> = {
  URGENT: 'Кабинет требует срочного вмешательства',
  NEEDS_ATTENTION: 'Кабинет требует внимания',
  NORMAL: 'Кабинет ведётся в норме',
  EXCELLENT: 'Кабинет ведётся хорошо',
};

export const SCORE_ZONE_TONE: Record<ScoreZoneKey, Severity> = {
  URGENT: 'BAD',
  NEEDS_ATTENTION: 'ALERT',
  NORMAL: 'GOOD',
  EXCELLENT: 'GOOD',
};

/** Неразрывный пробел escape-ом: так он виден в исходнике и находится grep'ом. */
const NBSP = '\u00A0';

/** Подпись слагаемого балла в разборе «из чего сложился». */
export const SCORE_PART_LABEL: Record<ScorePartKey, string> = {
  KPI_PLAN_FACT: `Выполнение плана по${NBSP}клиентам`,
  RISK_SHARE: `Доля бюджета под${NBSP}риском`,
  ZERO_RESULT: `Расход без${NBSP}обращений`,
  CRITICAL_INCIDENTS: 'Критичные инциденты',
  REGULAR_INCIDENTS: 'Обычные инциденты',
  ACTIVITY: 'Регулярность работы',
};

/**
 * Тот же словарь, но признающий, что ключ может не найтись.
 *
 * Индексировать надо через ослабленный тип: у `Record<ScorePartKey, string>`
 * значение по определению непустое, `@typescript-eslint/no-unnecessary-condition`
 * посчитает `?? key` лишним, и `eslint --fix` в pre-commit молча его выпилит.
 */
const KNOWN_SCORE_PART: {readonly [key: string]: string | undefined} = SCORE_PART_LABEL;

/**
 * Подпись слагаемого. Кода, которого страница не знает, быть не должно — но
 * схема уезжает вперёд релиза фронта, и пустая строка на этом месте читается
 * как сбой вёрстки. Печатаем сам код: «Итого» остаётся верным, а тот, кто
 * раскроет разбор, увидит незнакомое слагаемое, а не пустую строку. Разбор
 * свёрнут по умолчанию, поэтому простоять незамеченным код может долго —
 * это принято сознательно.
 */
export function scorePartLabel(key: ScorePartKey): string {
  return KNOWN_SCORE_PART[key] ?? key;
}

/**
 * Какой этаж отвечает за слагаемое балла.
 *
 * Разбор оценки и этажи отчёта говорят об одном и том же с разных сторон:
 * «расход без обращений» — это и вычет из балла, и целый раздел. Пока связь
 * между ними существовала только в голове читателя, разбор приходилось
 * раскрывать и сверять с разделами по названиям.
 *
 * Не все слагаемые ложатся на этажи один в один: инцидентов два — критичные
 * и обычные, — а этаж один, поэтому связь и описана отсюда-туда, а не
 * наоборот. Этажи без слагаемых (светофор, вопросы, задачи) в оценку не
 * входят вовсе, и приписывать им балл было бы выдумкой.
 */
export const SECTION_BY_SCORE_PART: Record<ScorePartKey, SectionId> = {
  KPI_PLAN_FACT: SECTION_IDS.clients,
  RISK_SHARE: SECTION_IDS.risks,
  ZERO_RESULT: SECTION_IDS.zeroResult,
  CRITICAL_INCIDENTS: SECTION_IDS.incidents,
  REGULAR_INCIDENTS: SECTION_IDS.incidents,
  ACTIVITY: SECTION_IDS.team,
};

/**
 * Тон слагаемого балла по доле набранного.
 *
 * Пороги те же, что и у зон самой оценки: ниже половины — провал, до
 * четырёх пятых — требует внимания, выше — норма.
 *
 * Правило живёт здесь, а не в разборе балла: тот же цвет теперь стоит и в
 * заголовке этажа, и разъехавшись, две отметки одного слагаемого начали бы
 * противоречить друг другу.
 *
 * Слагаемое без потолка тона не имеет: делить на ноль нечего, и красить
 * нечего тоже.
 */
const PART_BAD_BELOW = 0.5;
const PART_ALERT_BELOW = 0.8;

export function scorePartTone(got: number, max: number): PaletteTone {
  if (max <= 0) return 'alert';
  const share = got / max;
  if (share < PART_BAD_BELOW) return 'bad';
  if (share < PART_ALERT_BELOW) return 'alert';
  return 'good';
}

/* ────────────────────────── точка контроля ───────────────────────── */

/**
 * Подпись показателя в ожиданиях. `KPI` подписывается по метрике
 * медиаплана: у одного кабинета это цена обращения, у другого — доля
 * рекламных расходов.
 */
const EXPECTATION_METRIC_LABEL: Record<ExpectationMetric, string> = {
  CRITICAL_INCIDENTS: 'Критичные инциденты',
  KPI: 'Основной показатель',
  FEED_ERRORS: 'Ошибки товарного фида',
  MANUAL_EDIT_DAYS: 'Правки руками',
  TEAM_RESPONSE: 'Ответ команды',
};

export const KPI_LABEL: Record<KpiName, string> = {
  CPL: 'Цена обращения',
  DRR: 'Доля рекламных расходов',
};

export function expectationMetricLabel(
  metric: ExpectationMetric,
  kpiName?: KpiName | null,
): string {
  if (metric === 'KPI' && kpiName) return KPI_LABEL[kpiName];
  return EXPECTATION_METRIC_LABEL[metric];
}

/* ──────────────────────── светофор кампаний ──────────────────────── */

export interface CampaignColumn {
  color: CampaignColor;
  title: string;
  tone: Severity;
}

/**
 * Порядок значим: сначала то, что стоит денег, в конце то, что работает.
 * Читатель просматривает светофор слева направо и останавливается на
 * первой колонке — она должна быть красной.
 *
 * Серые кампании (без расхода за период) в светофоре не показываются:
 * колонка «ничего не тратили» не отвечает ни на один из трёх вопросов,
 * ради которых этаж существует.
 */
export const CAMPAIGN_COLUMNS: CampaignColumn[] = [
  {color: 'RED', title: 'Требуют вашего вопроса команде', tone: 'BAD'},
  {color: 'YELLOW', title: 'Держите на контроле', tone: 'ALERT'},
  {color: 'GREEN', title: 'Работают в плане', tone: 'GOOD'},
];

/* ───────────────────────────── навигация ─────────────────────────── */

/** Код этажа из `summary[].anchor` → якорь страницы. */
export const SECTION_BY_ANCHOR: Record<Section, SectionId> = {
  SCORE: SECTION_IDS.score,
  RISKS: SECTION_IDS.risks,
  CAMPAIGNS: SECTION_IDS.campaigns,
  CLIENTS: SECTION_IDS.clients,
  TEAM: SECTION_IDS.team,
  INCIDENTS: SECTION_IDS.incidents,
  QUESTIONS: SECTION_IDS.questions,
  TASKS: SECTION_IDS.tasks,
  CONTROL: SECTION_IDS.control,
};

/* ──────────────────────────── подписки ───────────────────────────── */

/** О чём предупредит подписка. */
export interface NotificationTopic {
  /** Лигатура Material Symbols. */
  icon: string;
  title: string;
  description: string;
}

export const NOTIFICATION_TOPICS: NotificationTopic[] = [
  {
    icon: 'event_available',
    title: 'Контрольный отчёт',
    description: 'Напомним за день до контрольной точки и пришлём отчёт, когда он будет готов.',
  },
  {
    icon: 'priority_high',
    title: 'Новые критичные ошибки',
    description: 'Сообщим в день появления — не через месяц, когда деньги уже потрачены.',
  },
  {
    icon: 'trending_up',
    title: 'Изменение оценки кабинета',
    description: 'Если балл вырастет или упадёт заметно, вы узнаете об этом сразу.',
  },
];

/* ──────────────────────── рекламные системы ──────────────────────── */

/** Подпись рекламной системы в реквизитах документа. */
export const AD_SYSTEM_LABEL: Record<string, string> = {
  YANDEX: 'Яндекс Директ',
  GOOGLE: 'Google Ads',
  FACEBOOK: 'Facebook',
  VKONTAKTE: 'ВКонтакте',
  VKONTAKTE_ADS: 'VK Реклама',
  METRIKA: 'Яндекс Метрика',
  WEBDAV: 'WebDav',
};

/**
 * Отчёт говорит месячными величинами, API — суточными.
 *
 * Умножение живёт одной константой: если завтра решим считать 28 дней
 * или брать длину периода, правка будет в одном месте.
 *
 * Множитель принадлежит фронту намеренно. Тексты, которые пишет модель,
 * сумм под риском не называют — расходиться с заголовком нечему, поэтому
 * переносить умножение на сервер незачем, а менять срок отсюда дешевле,
 * чем релизом бэкенда.
 */
export const RISK_DAYS = 30;

/**
 * Сколько кампаний показываем строками, остальные — в хвост.
 *
 * Описывает вёрстку, а не данные, и должна совпадать с тем, сколько
 * строк отдаёт сервер в `risks`.
 */
export const TOP_ROWS = 5;

/* ─────────────────── секция правок, которая не строилась ─────────── */

/**
 * Почему правок нет. Текст стоит на месте секции «Работа команды»:
 * пропавший раздел без объяснения читается как поломка страницы, а
 * полный балл за регулярность работы — как заслуга команды.
 */
const EDITS_UNMEASURED_REASON: Record<EditsStatusReason, string> = {
  PERIOD_BEFORE_TRACKING: `AdSensor начал вести проект позже начала периода, поэтому правки в${NBSP}кабинете за${NBSP}этот период не${NBSP}разбирались.`,
  HISTORY_UNAVAILABLE: `Журнал правок кабинета за${NBSP}этот период недоступен, поэтому правки не${NBSP}разбирались.`,
};

/** Причина не пришла — раздел всё равно обязан объяснить сам себя. */
const EDITS_UNMEASURED_FALLBACK = `Правки в${NBSP}кабинете за${NBSP}этот период не${NBSP}разбирались.`;

/**
 * Тот же словарь, но признающий, что кода может не найтись: схема уезжает
 * вперёд релиза фронта, и пустая карточка на этом месте хуже общей
 * формулировки. Индексируем через ослабленный тип — иначе
 * `@typescript-eslint/no-unnecessary-condition` посчитает `??` лишним, и
 * `eslint --fix` в pre-commit молча его выпилит.
 */
const KNOWN_EDITS_REASON: {readonly [key: string]: string | undefined} = EDITS_UNMEASURED_REASON;

export function editsUnmeasuredReason(reason?: EditsStatusReason | null): string {
  return (reason ? KNOWN_EDITS_REASON[reason] : undefined) ?? EDITS_UNMEASURED_FALLBACK;
}

/** Вторая фраза заглушки: объясняет полный балл за регулярность работы. */
export const EDITS_UNMEASURED_SCORE = `Регулярность работы команды никто не${NBSP}измерял${NBSP}— слагаемое «Регулярность работы» стоит в${NBSP}баллах${NBSP}полным.`;

/**
 * Запасная заметка слагаемого `ACTIVITY` в разборе балла. Нужна при
 * `narrativeFailed`: заметку обычно пишет генератор, а без него полный
 * балл остался бы без объяснения.
 */
export const ACTIVITY_FALLBACK_NOTE = `Правки за${NBSP}период не${NBSP}разбирались: регулярность работы не${NBSP}измерялась, и${NBSP}слагаемое стоит${NBSP}полным.`;
